import { circuitWebhookFilterSchema } from "./webhook-filter.js";

export const CIRCUIT_EVENT_SOURCES = ["linear", "github", "slack", "hubspot"] as const;
export type CircuitEventSource = (typeof CIRCUIT_EVENT_SOURCES)[number];

export const CIRCUIT_EVENT_CATALOG = {
  linear: {
    issue: ["state_change", "assignee_changed", "label_added"],
  },
  github: {
    pull_request: [
      "pull_request_opened",
      "pull_request_ready_for_review",
      "pull_request_merged",
      "pull_request_closed",
      "review_requested",
      "review_submitted",
      "check_failed",
    ],
    issue: [
      "issue_opened",
      "issue_reopened",
      "issue_closed",
      "issue_assigned",
      "issue_labeled",
      "issue_comment_created",
    ],
  },
  slack: {
    channel_message: ["message_posted"],
  },
  hubspot: {
    // Suggestions only: HubSpot subscription types are an open set.
    crm_object: ["deal.propertyChange", "contact.propertyChange", "company.propertyChange"],
  },
} as const;

export const CIRCUIT_EVENT_TEXT_OPERATORS = ["equals", "startsWith", "contains"] as const;

export type CircuitConditionField = {
  key: string;
  label: string;
  placeholder?: string;
  kind?: "text" | "select" | "boolean";
  options?: readonly { value: string; label: string }[];
};

const LINEAR_FIELDS: Record<string, readonly CircuitConditionField[]> = {
  state_change: [
    { key: "fromState", label: "Previous state", placeholder: "e.g. In Progress" },
    { key: "fromStateId", label: "Previous state ID" },
    { key: "fromStateType", label: "Previous state type", placeholder: "e.g. started" },
    { key: "toState", label: "Target state", placeholder: "e.g. Done" },
    { key: "toStateId", label: "Target state ID" },
    { key: "toStateType", label: "Target state type", placeholder: "e.g. completed" },
  ],
  assignee_changed: [
    { key: "assignee", label: "Assignee email" },
    { key: "assigneeName", label: "Assignee name" },
  ],
  label_added: [
    { key: "label", label: "Label name" },
    { key: "labelId", label: "Label ID" },
  ],
};

const GITHUB_COMMON_FIELDS: readonly CircuitConditionField[] = [
  { key: "actorLogin", label: "Actor login" },
  { key: "authorLogin", label: "Author login" },
];
const GITHUB_EVENT_FIELDS: Record<string, readonly CircuitConditionField[]> = {
  review_requested: [{ key: "reviewerLogin", label: "Reviewer login" }],
  review_submitted: [
    { key: "reviewerLogin", label: "Reviewer login" },
    {
      key: "reviewState",
      label: "Review state",
      kind: "select",
      options: [
        { value: "approved", label: "Approved" },
        { value: "changes_requested", label: "Changes requested" },
        { value: "commented", label: "Commented" },
      ],
    },
  ],
  check_failed: [{ key: "checkName", label: "Check name" }],
  issue_assigned: [{ key: "assigneeLogin", label: "Assignee login" }],
  issue_labeled: [{ key: "label", label: "Label" }],
  issue_comment_created: [{ key: "commenterLogin", label: "Commenter login" }],
};

const SLACK_FIELDS: readonly CircuitConditionField[] = [
  { key: "userId", label: "User ID" },
  { key: "botId", label: "Bot ID" },
  { key: "appId", label: "App ID" },
  { key: "workflowId", label: "Workflow ID" },
  { key: "templateId", label: "Template ID" },
  { key: "subtype", label: "Message subtype" },
  { key: "isThreadReply", label: "Thread reply", kind: "boolean" },
];

export function getCircuitEventEntityTypes(source: CircuitEventSource): string[] {
  return Object.keys(CIRCUIT_EVENT_CATALOG[source]);
}

export function getCircuitEventTypes(
  source: CircuitEventSource,
  entityType: string,
): readonly string[] {
  return (
    Object.entries(CIRCUIT_EVENT_CATALOG[source]).find(
      ([candidate]) => candidate === entityType,
    )?.[1] ?? []
  );
}

export function getCircuitConditionFields(
  source: CircuitEventSource,
  eventType: string,
): readonly CircuitConditionField[] {
  if (source === "linear") return LINEAR_FIELDS[eventType] ?? [];
  if (source === "slack") return SLACK_FIELDS;
  if (source === "hubspot") return [];
  const common =
    eventType === "check_failed" ? GITHUB_COMMON_FIELDS.slice(0, 1) : GITHUB_COMMON_FIELDS;
  return [...common, ...(GITHUB_EVENT_FIELDS[eventType] ?? [])];
}

type EventTriggerInput = {
  source?: unknown;
  entityType?: unknown;
  entityId?: unknown;
  entityIdentifier?: unknown;
  condition?: unknown;
  filter?: unknown;
  recurrent?: unknown;
  expiresAt?: unknown;
};

function invalidStringField(
  condition: Record<string, unknown>,
  field: CircuitConditionField,
): string | null {
  const value = condition[field.key];
  if (value === undefined) return null;
  if (field.kind === "boolean") {
    return typeof value === "boolean" ? null : `${field.label} must be a boolean`;
  }
  if (typeof value !== "string" || !value.trim()) {
    return `${field.label} must be a non-empty string`;
  }
  if (field.options && !field.options.some((option) => option.value === value)) {
    return `${field.label} must be one of: ${field.options.map((option) => option.value).join(", ")}`;
  }
  return null;
}

export function circuitEventTriggerValidationError(
  trigger: EventTriggerInput,
  options: { allowChannelWideSlack?: boolean } = {},
): string | null {
  const source = CIRCUIT_EVENT_SOURCES.find((candidate) => candidate === trigger.source);
  if (!source) return `Unsupported event source: ${String(trigger.source ?? "missing")}`;
  if (typeof trigger.entityType !== "string" || !trigger.entityType.trim()) {
    return "Event entity type is required";
  }
  if (trigger.entityType !== trigger.entityType.trim()) {
    return "Event entity type must not contain padding";
  }
  const eventTypes = getCircuitEventTypes(source, trigger.entityType);
  if (eventTypes.length === 0) {
    return `Unsupported ${source} event entity type: ${trigger.entityType}`;
  }
  if (
    !trigger.condition ||
    typeof trigger.condition !== "object" ||
    Array.isArray(trigger.condition)
  ) {
    return "Event trigger requires a condition";
  }
  const condition = trigger.condition as Record<string, unknown>;
  if (typeof condition.type !== "string" || !condition.type.trim()) {
    return "Event condition type is required";
  }
  if (source === "hubspot") {
    if (condition.type !== condition.type.trim()) {
      return "HubSpot subscription type must not contain padding";
    }
  } else if (!eventTypes.some((candidate) => candidate === condition.type)) {
    return `Unsupported ${source} event condition type ${condition.type} for ${trigger.entityType}`;
  }
  if (trigger.filter !== undefined) {
    if (source !== "hubspot") return "Property filters are only supported for HubSpot events";
    const result = circuitWebhookFilterSchema.safeParse(trigger.filter);
    if (!result.success) return `Invalid HubSpot filter: ${result.error.issues[0]!.message}`;
  }
  if (
    trigger.entityId !== undefined &&
    (typeof trigger.entityId !== "string" ||
      !trigger.entityId.trim() ||
      trigger.entityId !== trigger.entityId.trim())
  ) {
    return "Event entity ID must be a non-empty string without padding";
  }
  if (
    trigger.entityIdentifier !== undefined &&
    (typeof trigger.entityIdentifier !== "string" ||
      !trigger.entityIdentifier.trim() ||
      trigger.entityIdentifier !== trigger.entityIdentifier.trim())
  ) {
    return "Event entity identifier must be a non-empty string without padding";
  }
  if (trigger.entityIdentifier !== undefined && trigger.entityId === undefined) {
    return "Event entity identifier requires an entity ID";
  }
  if (trigger.recurrent !== undefined && typeof trigger.recurrent !== "boolean") {
    return "Event recurrent must be a boolean";
  }
  if (
    trigger.expiresAt !== undefined &&
    (typeof trigger.expiresAt !== "string" ||
      !trigger.expiresAt.trim() ||
      Number.isNaN(Date.parse(trigger.expiresAt)))
  ) {
    return "Event expiration must be a valid date-time string";
  }

  const fields = getCircuitConditionFields(source, condition.type);
  const allowedKeys = new Set(["type", ...fields.map((field) => field.key)]);
  if (source === "github") allowedKeys.add("repository");
  if (source === "slack") allowedKeys.add("text");
  const unknownKey = Object.keys(condition).find((key) => !allowedKeys.has(key));
  if (unknownKey) return `Unsupported ${source} event condition field: ${unknownKey}`;

  for (const field of fields) {
    const error = invalidStringField(condition, field);
    if (error) return error;
  }

  if (source === "github") {
    const repository = condition.repository;
    if (typeof repository !== "string" || !/^[^/\s]+\/[^/\s]+$/.test(repository)) {
      return 'GitHub circuit triggers require condition.repository in "owner/repo" format';
    }
    if (typeof trigger.entityId === "string") {
      const expectedPrefix = `${repository}#`.toLowerCase();
      const entityId = trigger.entityId.toLowerCase();
      if (
        !entityId.startsWith(expectedPrefix) ||
        !/^\d+$/.test(entityId.slice(expectedPrefix.length))
      ) {
        return `GitHub entity ID must use the "${repository}#123" format`;
      }
    }
  }

  if (source === "slack") {
    if (typeof trigger.entityId !== "string")
      return "Slack circuit triggers require a Slack channel ID";
    if (!/^[CG][A-Z0-9]+$/.test(trigger.entityId)) {
      return "Slack circuit trigger entity ID must be a Slack channel ID, not a channel name";
    }
    const text = condition.text;
    if (text !== undefined) {
      if (!text || typeof text !== "object" || Array.isArray(text)) {
        return "Slack text condition must be an object";
      }
      const textCondition = text as Record<string, unknown>;
      const unknownTextKey = Object.keys(textCondition).find(
        (key) => !["op", "value", "caseSensitive"].includes(key),
      );
      if (unknownTextKey) return `Unsupported Slack text condition field: ${unknownTextKey}`;
      if (!CIRCUIT_EVENT_TEXT_OPERATORS.some((candidate) => candidate === textCondition.op)) {
        return `Slack text op must be one of: ${CIRCUIT_EVENT_TEXT_OPERATORS.join(", ")}`;
      }
      if (typeof textCondition.value !== "string" || !textCondition.value.trim()) {
        return "Slack text condition value must be a non-empty string";
      }
      if (
        textCondition.caseSensitive !== undefined &&
        typeof textCondition.caseSensitive !== "boolean"
      ) {
        return "Slack text condition caseSensitive must be a boolean";
      }
    }
    const hasDiscriminator =
      ["userId", "botId", "appId", "workflowId", "templateId"].some(
        (key) => condition[key] !== undefined,
      ) || text !== undefined;
    if (!hasDiscriminator && !options.allowChannelWideSlack) {
      return "Slack circuit triggers require at least one sender, app, workflow, template, or text discriminator";
    }
  }

  return null;
}
